import {
  Matrix4,
  Mesh,
  Group,
  MeshPhysicalMaterial,
  DoubleSide,
  Vector3,
  Quaternion,
  Euler,
} from "three";
import { GLBDataV2 } from "./localTSReader";
import { updateElasticsState } from "./Elastics";
import { isBetween } from "./teethControls/TeethPositionHelper";
import { Jaw } from "./interfaces.ts";
import { occlusalAreaManager } from "./Mesh.jsx";
import { globalData } from "../../helpers/globData.ts";
import { BJStepHeatmap, isBiteJumpActive } from "./Bitejump.ts";
import { SetObjectMatrix, SetObjectMatrixWorld } from "./MatrixHelper.ts";

export interface TreatmentPlanGLB {
  /**
   * this is the glb of the tooth crown + roots (Relu's export) for now
   */
  rootGLB: {};
  /**
   * this is the glb of the tooth crown (the base export of 3Shape) for now
   */
  baseGLB: { teeth: {}; gums: {} };
}

interface GingivaData {
  index: number[];
  positions: number[];
}

interface OTP {
  Stages: [];
}

interface GingivaDataArches {
  lower: GingivaData;
  upper: GingivaData;
}

interface Gums {
  lower: Mesh;
  upper: Mesh;
}

interface toothStepData {
  movementMatrix: Matrix4;
  attachment?: attachmentStepData[];
}

interface attachmentStepData {
  type: string;
  movementMatrix: Matrix4;
}

export class TreatmentSetup {
  teeth: Mesh[];
  extractedTeeth: Set<Mesh>;
  teethMovementMatrix: toothStepData[][];
  otp: object;
  currentStep: number;
  bones: { skull: Mesh; mandible: Mesh };
  gum: Gums;
  gingivaData: GingivaDataArches[];
  steps: Group[];
  name: string;
  heatmaps: any;
  controlsHeatmaps: any;
  static extractedTeethMaterial: MeshPhysicalMaterial =
    new MeshPhysicalMaterial({
      roughness: 0.4,
      reflectivity: 8,
      specularIntensity: 3,
      specularColor: 0x2e2e2e,
      emissiveIntensity: 1,

      color: "#fff9f9",
      emissive: "#303030",
      side: DoubleSide,
      flatShading: false,
      clearcoat: 0.8,
      // renderOrder: 10,
      name: "extractedTeethMaterial",
      transparent: true,
      opacity: 0.5,
    });
  static teethMaterial: MeshPhysicalMaterial = new MeshPhysicalMaterial({
    roughness: 0.4,
    reflectivity: 8,
    specularIntensity: 3,
    specularColor: 0x2e2e2e,
    emissiveIntensity: 1,
    color: "#fff9f9",
    emissive: "#303030",
    side: DoubleSide,
    flatShading: false,
    clearcoat: 0.8,
    // renderOrder: 10,
    name: "teethMaterial",
  });
  constructor(
    teeth = [],
    otp: OTP = { Stages: [] },
    steps: Group[] = [],
    name: string = "",
    bones: { skull: Mesh; mandible: Mesh },
    gingiva = [],
    gingivaData?: GingivaDataArches[],
  ) {
    this.teeth = teeth;
    this.otp = otp;
    this.currentStep = 0;
    this.teethMovementMatrix = [];
    this.extractedTeeth = new Set();
    this.steps = steps;
    this.name = name;
    this.bones = bones;
    this.controlsHeatmaps = {};
    this.prepareTeethMovementMatrix(otp);
    this.moveToStep(0, true);

    // this.gum = { lower: gingiva[0], upper: gingiva[1] };
    // this.gingivaData = gingivaData;
  }

  isValidStep(stepNumber: number): boolean {
    return isBetween(stepNumber, 0, this.steps.length - 2);
  }
  moveToStep(stepNumber: number, noBack?: boolean): void {
    const startTime = Date.now();
    // console.log(`Move ${this.name} to step ${stepNumber}`);
    if (!this.isValidStep(stepNumber)) {
      console.error(`Step ${stepNumber} does not exist in ts`);
      return;
    }

    if (stepNumber !== this.currentStep || noBack) {
      //#region teeth

      let matrix4: Matrix4 | undefined;
      let toothNum: number;
      let matrixArray: toothStepData[];
      //#region reverse teeth back to base state

      if (!noBack) {
        this.teeth.forEach(tooth => {
          if (tooth.userData["pontic"]) {
            return;
          }
          // toothIndex = parseInt(tooth.name);
          toothNum = tooth.userData["number"];

          if (this.teethMovementMatrix[toothNum]) {
            tooth.children[0].remove(...tooth.children[0].children);
            matrixArray = this.teethMovementMatrix[toothNum] as toothStepData[];
            matrix4 = matrixArray?.[this.currentStep]?.movementMatrix;
            if (matrix4 instanceof Matrix4) {
              tooth.children[1].applyMatrix4(matrix4);
              tooth.children[2].applyMatrix4(matrix4);
              matrix4 = matrix4.clone().invert();

              tooth.applyMatrix4(matrix4);
              tooth.updateMatrixWorld();
              // tooth.updateMatrixWorld(false);
            }
          }
        });
      }
      //#endregion
      //#region extracted Teeth
      const extractedTeethArray = Array.from(this.extractedTeeth);
      const replantedTeeth = [];

      for (
        let extractedtoothIndex = 0;
        extractedtoothIndex < extractedTeethArray.length;
        extractedtoothIndex++
      ) {
        const extractedtooth = extractedTeethArray[extractedtoothIndex];

        toothNum = extractedtooth.userData["number"];

        if (this.teethMovementMatrix[toothNum]) {
          matrixArray = this.teethMovementMatrix[toothNum] as toothStepData[];

          matrix4 = matrixArray?.[stepNumber]?.movementMatrix;
          if (matrix4 instanceof Matrix4) {
            this.assignAttachmentToTooth(
              extractedtooth,
              matrixArray?.[stepNumber].attachment,
            );
            extractedtooth.applyMatrix4(matrix4);
            extractedtooth.children[2].applyMatrix4(matrix4.clone().invert());
            extractedtooth.children[1].applyMatrix4(matrix4.clone().invert());
            // extractedtooth.updateMatrixWorld(false);

            extractedtooth.userData["isExtracted"] = false;
            extractedtooth.material = TreatmentSetup.teethMaterial;

            replantedTeeth.push(extractedtooth);
            this.extractedTeeth.delete(extractedtooth);
          } else {
            if (extractedtooth instanceof Mesh) {
              extractedtooth.userData["isExtracted"] = true;
            }
          }
        }
      }

      //#endregion
      //#region move teeth to the passed {stepNumber}
      this.teeth.forEach(tooth => {
        // toothIndex = parseInt(tooth.name);+
        if (tooth.userData["pontic"]) {
          return;
        }

        toothNum = tooth.userData["number"];

        if (this.teethMovementMatrix[toothNum]) {
          matrixArray = this.teethMovementMatrix[toothNum] as toothStepData[];

          matrix4 = matrixArray?.[stepNumber]?.movementMatrix;
          if (matrix4 instanceof Matrix4) {
            this.assignAttachmentToTooth(
              tooth,
              matrixArray?.[stepNumber].attachment,
            );
            tooth.children[1].applyMatrix4(matrix4.clone().invert());
            tooth.children[2].applyMatrix4(matrix4.clone().invert());
            tooth.applyMatrix4(matrix4);
            tooth.updateMatrixWorld();

            // if (tooth.children[0] instanceof Mesh) {
            //   tooth.material = TreatmentSetup.teethMaterial;
            // }
            tooth.userData["isExtracted"] = false;
          } else {
            if (tooth instanceof Mesh) {
              tooth.userData["isExtracted"] = true;

              this.extractedTeeth.add(tooth);
              tooth.material = TreatmentSetup.extractedTeethMaterial;
            }
          }
        }
      });
      //#endregion
      //#endregion

      if (this.gingivaData) {
        //#region gingiva
        //#lower section

        const worldPosition = this.gum.lower.getWorldPosition(new Vector3());

        for (
          let index = 0, posIndex = 0;
          index < this.gingivaData[stepNumber].lower.index.length;
          index++
        ) {
          posIndex = this.gingivaData[stepNumber].lower.index[index] * 3;
          // console.log(
          //   `replace lower geom of pos ${posIndex} to gingivaData of step ${stepNumber} of index ${index * 3}`,
          // );
          this.gum.lower.geometry.attributes.position.array[posIndex] =
            this.gingivaData[stepNumber].lower.positions[index * 3];
          this.gum.lower.geometry.attributes.position.array[posIndex + 1] =
            this.gingivaData[stepNumber].lower.positions[index * 3 + 1];
          this.gum.lower.geometry.attributes.position.array[posIndex + 2] =
            this.gingivaData[stepNumber].lower.positions[index * 3 + 2];
        }
        this.gum.lower.geometry.attributes.position.needsUpdate = true;
        //#endregion
        //#region upper

        const upperGingivaWorldPosition = this.gum.lower.getWorldPosition(
          new Vector3(),
        );

        for (
          let index = 0, posIndex = 0;
          index < this.gingivaData[stepNumber].upper.index.length;
          index++
        ) {
          posIndex = this.gingivaData[stepNumber].upper.index[index] * 3;
          this.gum.upper.geometry.attributes.position.array[posIndex] =
            this.gingivaData[stepNumber].upper.positions[index * 3];
          this.gum.upper.geometry.attributes.position.array[posIndex + 1] =
            this.gingivaData[stepNumber].upper.positions[index * 3 + 1];
          this.gum.upper.geometry.attributes.position.array[posIndex + 2] =
            this.gingivaData[stepNumber].upper.positions[index * 3 + 2];
        }

        this.gum.upper.geometry.attributes.position.needsUpdate = true;
        //#endregion

        //#endregion
      }

      //#region moveParenting
      this.moveTeethFromStepToStep(
        this.steps[this.currentStep],
        this.steps[stepNumber],
      );

      this.moveTeethToStep(replantedTeeth, this.steps[stepNumber]); // moving extracted teeth back

      this.teeth = [
        ...this.steps[stepNumber].children[0].children,
        ...this.steps[stepNumber].children[1].children,
      ] as Mesh[];

      this.teeth.forEach(tooth => {
        tooth.children[1].visible = globalData.isActive.numbering;
      });

      this.steps[stepNumber].visible = true;
      this.steps[this.currentStep].visible = false;

      //#endregion

      //#region Elastic Update

      this.updateElasticsState(stepNumber);
      //#endregion

      if (this.heatmaps) {
        this.updateHeatMaps(stepNumber);
      }

      this.currentStep = stepNumber;
      // console.log(`${this.name} current step ${this.currentStep}`);
    }
  }

  getStepHeatMap(index: number) {
    return index === this.heatmaps.length - 1 && isBiteJumpActive
      ? BJStepHeatmap[0]
      : this.heatmaps[index];
  }
  updateHeatMaps(heatmapsIndex: number, is3DControls: boolean = false) {
    if (occlusalAreaManager) {
      return;
    }
    const stepHeatmaps = !is3DControls
      ? this.getStepHeatMap(heatmapsIndex)
      : this.controlsHeatmaps;

    if (stepHeatmaps) {
      for (
        let toothNumber = 0;
        toothNumber < this.teeth.length;
        toothNumber++
      ) {
        const tooth = this.teeth[toothNumber];

        if (
          stepHeatmaps[tooth.userData["number"]] &&
          stepHeatmaps[tooth.userData["number"]]["color"]
        ) {
          tooth.geometry.attributes.color =
            stepHeatmaps[tooth.userData["number"]].color;
        }
      }
    }
  }
  updateElasticsState(stepNumber: number): void {
    this.teeth.forEach(tooth => {
      updateElasticsState(tooth, stepNumber);
    });
  }
  moveTeethFromStepToStep(currentStep: Group, nextStep: Group) {
    for (let jawIndex = 0; jawIndex < 2; jawIndex++) {
      for (
        let indexCurrent = 0;
        indexCurrent < currentStep.children[jawIndex].children.length;
        indexCurrent++
      ) {
        const currentTooth: Mesh = currentStep.children[jawIndex].children[
          indexCurrent
        ] as Mesh;
        if (
          currentTooth.userData["pontic"] ||
          currentTooth.userData["isExtracted"]
        ) {
          continue;
        }
        let nextTooth: Mesh;
        for (
          let indexNext = 0;
          indexNext < nextStep.children[jawIndex].children.length;
          indexNext++
        ) {
          nextTooth = nextStep.children[jawIndex].children[indexNext] as Mesh;
          if (
            nextTooth.userData["pontic"] ||
            currentTooth.userData["isExtracted"]
          ) {
            continue;
          }
          if (
            currentTooth.userData["number"] === nextTooth.userData["number"]
          ) {
            this.swapToothWithMesh(
              currentTooth,
              nextTooth,
              indexCurrent,
              indexNext,
            );
            break;
          }
        }
      }
    }
  }
  moveTeethToStep(teeth: Mesh[], nextStep: Group) {
    for (let indexCurrent = 0; indexCurrent < teeth.length; indexCurrent++) {
      const currentTooth = teeth[indexCurrent];
      const jawIndex = currentTooth.userData["jaw"] === Jaw.Upper ? 0 : 1;
      let nextTooth: Mesh;
      for (
        let indexNext = 0;
        indexNext < nextStep.children[jawIndex].children.length;
        indexNext++
      ) {
        nextTooth = nextStep.children[jawIndex].children[indexNext] as Mesh;
        if (
          nextTooth.userData["pontic"] ||
          currentTooth.userData["isExtracted"]
        ) {
          continue;
        }
        if (currentTooth.userData["number"] === nextTooth.userData["number"]) {
          this.swapToothWithMesh(
            currentTooth,
            nextTooth,
            currentTooth.parent.children.indexOf(currentTooth),
            indexNext,
          );
          break;
        }
      }
    }
  }
  swapToothWithMesh(
    currentTooth: Mesh,
    nextTooth: Mesh,
    currentIndex: number,
    nextIndex: number,
  ): void {
    const currentIPR = currentTooth.children[2],
      nextIPR = nextTooth.children[2];
    const currentNumbering = currentTooth.children[1],
      nextNumbering = nextTooth.children[1];

    const currentIPRMatrix = currentIPR.matrixWorld.clone(),
      currentNumberingMatrix = currentNumbering.matrixWorld.clone(),
      nextIPRMatrix = nextIPR.matrixWorld.clone(),
      nextNumberingMatrix = nextNumbering.matrixWorld.clone();
    this.swapChildrenAdd(currentTooth, nextTooth, currentIndex, nextIndex);
    this.swapName(currentTooth, nextTooth);

    this.swapChildrenAttach(
      currentNumbering as Mesh,
      nextNumbering as Mesh,
      1,
      1,
    ); // swap Numbers

    this.swapChildrenAttach(currentIPR as Mesh, nextIPR as Mesh, 2, 2); // swap IPRs

    currentTooth.parent.updateMatrixWorld();

    SetObjectMatrixWorld(nextNumbering, nextNumberingMatrix);
    SetObjectMatrixWorld(currentNumbering, currentNumberingMatrix);

    SetObjectMatrixWorld(nextIPR, nextIPRMatrix);
    SetObjectMatrixWorld(currentIPR, currentIPRMatrix);

    // currentIPR.applyMatrix4(currentIPRMatrix);
    const currentUserData = currentTooth.userData;
    const nextUserData = nextTooth.userData;

    currentTooth.userData = nextUserData;
    nextTooth.userData = currentUserData;
    if (nextTooth.userData["invertedStartMatrix"]) {
      currentTooth.userData["invertedStartMatrix"] =
        nextTooth.userData["invertedStartMatrix"].clone();
    }
  }

  reverseRotation(currentTooth: Mesh): void {
    currentTooth.children[2].rotation.copy(
      new Euler().setFromRotationMatrix(currentTooth.matrix.clone().invert()),
    );
  }
  swapName(currentTooth: Mesh, nextTooth: Mesh) {
    const currentName = currentTooth.name;
    const nextName = nextTooth.name;

    currentTooth.name = nextName;
    nextTooth.name = currentName;
  }

  swapChildren(
    firstObject: Mesh,
    secondObject: Mesh,
    firstIndex: number,
    secondIndex: number,
  ): void {
    const firstParent = firstObject.parent;
    const secondParent = secondObject.parent;

    firstParent.children.splice(firstIndex, 1, secondObject);
    secondParent.children.splice(secondIndex, 1, firstObject);

    secondObject.parent = firstParent;
    firstObject.parent = secondParent;

    // firstParent.attach(secondObject);
    // secondParent.attach(firstObject);
  }

  swapChildrenAttach(
    firstObject: Mesh,
    secondObject: Mesh,
    firstIndex: number,
    secondIndex: number,
  ): void {
    const firstParent = firstObject.parent;
    const secondParent = secondObject.parent;

    const firstParentChildren = [...firstParent.children];
    const secondParentChildren = [...secondParent.children];

    for (let index = 0; index < firstParentChildren.length; index++) {
      firstParent.attach(
        index === firstIndex
          ? secondParentChildren[secondIndex]
          : firstParentChildren[index],
      );
    }
    for (let index = 0; index < secondParentChildren.length; index++) {
      secondParent.attach(
        index === secondIndex
          ? firstParentChildren[firstIndex]
          : secondParentChildren[index],
      );
    }
  }

  swapChildrenAdd(
    firstObject: Mesh,
    secondObject: Mesh,
    firstIndex: number,
    secondIndex: number,
  ): void {
    const firstParent = firstObject.parent;
    const secondParent = secondObject.parent;

    const firstParentChildren = [...firstParent.children];
    const secondParentChildren = [...secondParent.children];

    for (let index = 0; index < firstParentChildren.length; index++) {
      firstParent.add(
        index === firstIndex
          ? secondParentChildren[secondIndex]
          : firstParentChildren[index],
      );
    }
    for (let index = 0; index < secondParentChildren.length; index++) {
      secondParent.add(
        index === secondIndex
          ? firstParentChildren[firstIndex]
          : secondParentChildren[index],
      );
    }
  }

  swapChildrenFreezed2(
    firstObject: Mesh,
    secondObject: Mesh,
    firstIndex: number,
    secondIndex: number,
  ): void {
    const parent1 = firstObject.parent;
    const parent2 = secondObject.parent;

    if (!parent1 || !parent2) {
      console.error(
        "swapChildrenFreezed: One or both objects do not have a parent. Swap cannot be performed.",
      );
      return;
    }

    // --- 1. Store World Transforms of Both Children ---
    // Ensure matrices are up-to-date before getting world transforms
    firstObject.updateMatrixWorld(true);
    secondObject.updateMatrixWorld(true);

    const child1WorldPosition = new Vector3();
    const child1WorldQuaternion = new Quaternion();
    const child1WorldScale = new Vector3();
    firstObject.getWorldPosition(child1WorldPosition);
    firstObject.getWorldQuaternion(child1WorldQuaternion);
    firstObject.getWorldScale(child1WorldScale);

    const child2WorldPosition = new Vector3();
    const child2WorldQuaternion = new Quaternion();
    const child2WorldScale = new Vector3();
    secondObject.getWorldPosition(child2WorldPosition);
    secondObject.getWorldQuaternion(child2WorldQuaternion);
    secondObject.getWorldScale(child2WorldScale);

    // --- 2. Detach Children from Current Parents ---
    // Ensure parent matrices are up-to-date before detaching children
    // (though already done by child.updateMatrixWorld(true) if parents were part of that chain)
    parent1.updateMatrixWorld(true);
    if (parent1 !== parent2) {
      parent2.updateMatrixWorld(true);
    }

    parent1.remove(firstObject);
    parent2.remove(secondObject); // Works even if parent1 === parent2, as it operates on the modified children array.

    // --- 3. Attach Children to New Parents at Specified Indices ---
    // secondObject goes into parent1 at firstIndex
    // firstObject goes into parent2 at secondIndex

    // Add secondObject to parent1
    if (firstIndex < 0 || firstIndex > parent1.children.length) {
      console.warn(
        `swapChildrenFreezed: firstIndex ${firstIndex} is out of bounds for parent1. Appending instead.`,
      );
      parent1.add(secondObject); // Fallback: add to end
    } else {
      parent1.children.splice(firstIndex, 0, secondObject);
      secondObject.parent = parent1; // Manually set parent after splice
    }

    // Add firstObject to parent2
    // Note: If parent1 === parent2, parent2.children is the same array as parent1.children,
    // which has already been modified by the first splice. The secondIndex still refers to
    // the desired position in that array.
    if (secondIndex < 0 || secondIndex > parent2.children.length) {
      console.warn(
        `swapChildrenFreezed: secondIndex ${secondIndex} is out of bounds for parent2. Appending instead.`,
      );
      parent2.add(firstObject); // Fallback: add to end
    } else {
      parent2.children.splice(secondIndex, 0, firstObject);
      firstObject.parent = parent2; // Manually set parent after splice
    }

    // --- 4. Reapply World Transforms ---
    // The children are now in their new parent hierarchies, but their local transforms are default.
    // We need to calculate their new local transforms so their world transforms match the stored ones.

    // For firstObject (now a child of parent2, or parent1 if they were the same)
    const finalParentOfFirstObject = firstObject.parent;
    if (finalParentOfFirstObject) {
      finalParentOfFirstObject.updateMatrixWorld(true); // Ensure new parent's matrixWorld is current
      const invFinalParent1Matrix = new Matrix4()
        .copy(finalParentOfFirstObject.matrixWorld)
        .invert();

      firstObject.matrix.identity(); // Reset local matrix
      firstObject.applyMatrix4(invFinalParent1Matrix); // Transform to new parent's local space
      // Then apply original world transform
      firstObject.applyMatrix4(
        new Matrix4().compose(
          child1WorldPosition,
          child1WorldQuaternion,
          child1WorldScale,
        ),
      );
      // Decompose the new local matrix into local position, quaternion, scale
      firstObject.matrix.decompose(
        firstObject.position,
        firstObject.quaternion,
        firstObject.scale,
      );
      firstObject.updateMatrixWorld(true); // Update its world matrix and for its children
    } else {
      console.error(
        "swapChildrenFreezed: firstObject lost its parent during swap, cannot reapply transform.",
      );
    }

    // For secondObject (now a child of parent1, or parent2 if they were the same)
    const finalParentOfSecondObject = secondObject.parent;
    if (finalParentOfSecondObject) {
      finalParentOfSecondObject.updateMatrixWorld(true); // Ensure new parent's matrixWorld is current
      const invFinalParent2Matrix = new Matrix4()
        .copy(finalParentOfSecondObject.matrixWorld)
        .invert();

      secondObject.matrix.identity(); // Reset local matrix
      secondObject.applyMatrix4(invFinalParent2Matrix); // Transform to new parent's local space
      // Then apply original world transform
      secondObject.applyMatrix4(
        new Matrix4().compose(
          child2WorldPosition,
          child2WorldQuaternion,
          child2WorldScale,
        ),
      );
      // Decompose the new local matrix into local position, quaternion, scale
      secondObject.matrix.decompose(
        secondObject.position,
        secondObject.quaternion,
        secondObject.scale,
      );
      secondObject.updateMatrixWorld(true); // Update its world matrix and for its children
    } else {
      console.error(
        "swapChildrenFreezed: secondObject lost its parent during swap, cannot reapply transform.",
      );
    }
  }
  /**
   * swap children without changing thier transform
   * @param firstObject
   * @param secondObject
   * @param firstIndex
   * @param secondIndex
   */
  swapChildrenFreezed(
    firstObject: Mesh,
    secondObject: Mesh,
    firstIndex: number,
    secondIndex: number,
  ): void {
    // const worldPositionFirst = new Vector3();
    // const worldQuaternionFirst = new Quaternion();
    // const worldScaleFirst = new Vector3();

    // firstObject.getWorldPosition(worldPositionFirst);
    // firstObject.getWorldQuaternion(worldQuaternionFirst);
    // firstObject.getWorldScale(worldScaleFirst);

    // const worldPositionSecond = new Vector3();
    // const worldQuaternionSecond = new Quaternion();
    // const worldScaleSecond = new Vector3();

    // secondObject.getWorldPosition(worldPositionSecond);
    // secondObject.getWorldQuaternion(worldQuaternionSecond);
    // secondObject.getWorldScale(worldScaleSecond);

    const firstParent = firstObject.parent;
    const secondParent = secondObject.parent;
    firstParent.attach(secondObject);
    secondParent.attach(firstObject);

    // firstParent.children.splice(firstIndex, 1, secondObject);
    // secondParent.children.splice(secondIndex, 1, firstObject);

    // secondObject.parent = firstParent;
    // firstObject.parent = secondParent;

    // firstObject.position.copy(worldPositionFirst);
    // firstObject.quaternion.copy(worldQuaternionFirst);
    // firstObject.scale.copy(worldScaleFirst);

    // secondObject.position.copy(worldPositionSecond);
    // secondObject.quaternion.copy(worldQuaternionSecond);
    // secondObject.scale.copy(worldScaleSecond);
    // console.log(
    //   firstObject.parent.parent.parent.position,
    //   firstObject.parent.parent.position,
    //   firstObject.parent.position,

    //   secondObject.parent.parent.parent.position,
    //   secondObject.parent.parent.position,
    //   secondObject.parent.position,
    // );

    // firstObject.position.add(secondObject.parent.position);
    // secondObject.position.sub(secondObject.parent.position);
    // // firstParent.attach(secondObject);
    // // secondParent.attach(firstObject);
    // firstObject.updateMatrixWorld(true);
    // secondObject.updateMatrixWorld(true);
    // console.log(firstObject.children.length, firstObject);
  }

  swapChildrenWithPreservedIndices(
    firstObject: Mesh,
    secondObject: Mesh,
    firstIndex: number,
    secondIndex: number,
  ): void {
    // Store world-space transforms before changing parents
    const firstWorldPosition = new Vector3();
    const firstWorldQuaternion = new Quaternion();
    const firstWorldScale = new Vector3();

    const secondWorldPosition = new Vector3();
    const secondWorldQuaternion = new Quaternion();
    const secondWorldScale = new Vector3();

    firstObject.getWorldPosition(firstWorldPosition);
    firstObject.getWorldQuaternion(firstWorldQuaternion);
    firstObject.getWorldScale(firstWorldScale);

    secondObject.getWorldPosition(secondWorldPosition);
    secondObject.getWorldQuaternion(secondWorldQuaternion);
    secondObject.getWorldScale(secondWorldScale);

    // Get current parents
    const firstParent = firstObject.parent;
    const secondParent = secondObject.parent;
    firstParent.updateMatrixWorld();
    secondParent.updateMatrixWorld();

    // Swap children while maintaining indices
    firstParent.children.splice(firstIndex, 1, secondObject);
    secondParent.children.splice(secondIndex, 1, firstObject);

    // Update parents
    secondObject.parent = firstParent;
    firstObject.parent = secondParent;

    const firstParentInverse = firstParent.matrixWorld.invert();
    const secondParentInverse = secondParent.matrixWorld.invert();

    // Create temporary matrices for transform application
    const tempMatrix1 = new Matrix4();
    const tempMatrix2 = new Matrix4();

    // Apply transforms with parent matrix compensation
    tempMatrix1.compose(
      firstWorldPosition,
      firstWorldQuaternion,
      firstWorldScale,
    );
    tempMatrix1.premultiply(firstParentInverse);
    firstObject.matrix.copy(tempMatrix1);

    tempMatrix2.compose(
      secondWorldPosition,
      secondWorldQuaternion,
      secondWorldScale,
    );
    tempMatrix2.premultiply(secondParentInverse);
    secondObject.matrix.copy(tempMatrix2);

    // Restore world-space transforms
    firstObject.position.copy(firstWorldPosition);
    firstObject.quaternion.copy(firstWorldQuaternion);
    firstObject.scale.copy(firstWorldScale);

    secondObject.position.copy(secondWorldPosition);
    secondObject.quaternion.copy(secondWorldQuaternion);
    secondObject.scale.copy(secondWorldScale);

    // Update matrices to ensure proper transform propagation
    firstObject.updateMatrixWorld(true);
    secondObject.updateMatrixWorld(true);
  }
  prepareTeethMovementMatrix(otp: OTP): void {
    this.teeth.forEach((tooth, index) => {
      const teethNum: number = tooth.userData["number"];
      this.teethMovementMatrix[teethNum] = [];

      otp.Stages.forEach((step: any, stepIndex: number) => {
        const teethMovementMatrix = new Matrix4();

        if (!step?.Teeth[index]?.Movement?.Value?.Transformation) {
          console.log("otp step doesn't have tooth movement value");
          return;
        }

        const val: number[][] = step.Teeth[index].Movement.Value.Transformation;
        teethMovementMatrix.set(
          val[0][0],
          val[0][1],
          val[0][2],
          val[0][3],
          val[1][0],
          val[1][1],
          val[1][2],
          val[1][3],
          val[2][0],
          val[2][1],
          val[2][2],
          val[2][3],
          val[3][0],
          val[3][1],
          val[3][2],
          val[3][3],
        );
        this.teethMovementMatrix[teethNum][stepIndex] = {
          movementMatrix: teethMovementMatrix,
          attachment: [],
        };

        step.Teeth[index].Attachments.forEach(attData => {
          const attMovementMatrix = new Matrix4();
          const val: number[][] = attData.InitialPositionOnNonTransformedTooth;
          attMovementMatrix.set(
            val[0][0],
            val[0][1],
            val[0][2],
            val[0][3],
            val[1][0],
            val[1][1],
            val[1][2],
            val[1][3],
            val[2][0],
            val[2][1],
            val[2][2],
            val[2][3],
            val[3][0],
            val[3][1],
            val[3][2],
            val[3][3],
          );
          this.teethMovementMatrix[teethNum][stepIndex].attachment.push({
            type: this.replaceSpacesWithUnderscores(
              attData.AdditiveModelID.slice(0, -4),
            ),
            movementMatrix: attMovementMatrix,
          });
        });
      });
    });
  }

  assignExtractedTeethMaterial(tooth: Mesh): void {
    // if()
  }
  replaceSpacesWithUnderscores(str: string): string {
    return str.replace(/ /g, "_");
  }
  assignAttachmentToTooth(tooth: Mesh, ASD: attachmentStepData[]): void {
    ASD.forEach(attData => {
      const att = GLBDataV2.baseAttGLB[attData.type].clone();

      att.applyMatrix4(attData.movementMatrix);
      tooth.children[0].add(att);
      // tooth.children[0].attach(att);
    });
  }
  getStep(stepIndex: number): any {
    if (isBetween(stepIndex, 0, this.steps.length)) {
      return this.steps[stepIndex];
    } else {
      console.log(`Step ${stepIndex} is out of range`);
      return undefined;
    }
  }
  getCurrentStep() {
    return this.getStep(this.currentStep);
  }
}
